// The replay link of the season chronicle, on the game side
// (chronicle.md §8, R1181).
//
// The book in the hub links a battle line of a live volume to
// `/?world=<world>&replay=<ref>`. The entry joins that world as a deep link
// does (`entry.ts`), and this module asks for the film once the first
// Empire frame has come: the player is then seated, and the inbox intent
// is the one a REPLAY press on a battle mark sends (R531).
//
// The server answers a stale reference with the event `replay_gone`
// (`hub.fetchReplay`). A reply the hub dropped leaves the page with no
// answer at all, so a wait of `replay_link_wait_ms` ends in a plain notice
// and never in a hang. Every other notice leaves the wait running (R1374).

import { EVENT_REPLAY_GONE } from '../gen/balance';
import type { Handlers } from '../net/connection';
import type { ReplayRef } from '../gen/proto/aquaphobia';
import type { ReplayLink } from '../ui/hub/chronicle/address';

export interface ReplayLinkDeps {
  /** Sends the inbox intent of the film. */
  send: (ref: ReplayRef) => void;
  /** Says that no film came. */
  silent: () => void;
  /** The server refused the reference (`replay_gone`). The notice still
   *  reaches the feed after this call (R1684). */
  gone?: () => void;
  /** How long the page waits for an answer, in ms. */
  waitMs: number;
  setTimer?: (fn: () => void, ms: number) => unknown;
  clearTimer?: (id: unknown) => void;
}

/**
 * Wraps three handlers of the feed so the first Empire frame asks for the
 * film of `link`. The wait ends on the Replay frame, or on the refusal of
 * `hub.fetchReplay`, which is the one notice with the event id
 * `replay_gone` (R1374). The link acts one time: a reconnect does not ask
 * again.
 */
export function armReplayLink(h: Handlers, link: ReplayLink, d: ReplayLinkDeps): void {
  const setTimer = d.setTimer ?? ((fn, ms) => setTimeout(fn, ms));
  const clearTimer = d.clearTimer ?? ((id) => clearTimeout(id as ReturnType<typeof setTimeout>));
  let state: 'armed' | 'waiting' | 'done' = 'armed';
  let timer: unknown;
  const answered = (): void => {
    if (state !== 'waiting') return;
    state = 'done';
    clearTimer(timer);
  };

  const onEmpire = h.onEmpire.bind(h);
  h.onEmpire = (e) => {
    onEmpire(e);
    if (state !== 'armed') return;
    state = 'waiting';
    d.send(link.ref);
    timer = setTimer(() => {
      if (state !== 'waiting') return;
      state = 'done';
      d.silent();
    }, d.waitMs);
  };
  const onReplay = h.onReplay.bind(h);
  h.onReplay = (r) => { answered(); onReplay(r); };
  const onNotice = h.onNotice.bind(h);
  h.onNotice = (n) => {
    if (n.event === EVENT_REPLAY_GONE && state === 'waiting') {
      answered();
      d.gone?.();
    }
    onNotice(n);
  };
}
